// =============================================================================
// PDF preview renderer.
//
// Runs on pdf-preview.html (the lead-magnet page surfaced after a successful
// email subscription on results.html). Reads the stored inputs from
// sessionStorage, runs the engine, and binds the personalized result to the
// data-ff-bind slots. Reuses the same renderer used by results.ts so the PDF
// content always matches the on-results-page narrative for the same inputs.
//
// Two gates run before any rendering:
//   1. Email gate. The user must have submitted their email on results.ts —
//      the subscribe handler in scripts.js sets sessionStorage
//      'ff-pdf-unlocked' to 'true' on a successful Beehiiv response and
//      redirects here. Direct navigation without that flag bounces the user
//      back to /results with a "submit your email first" hint.
//   2. Inputs gate. If the user somehow reaches this page without lab
//      inputs in sessionStorage, the renderer falls back to a friendly
//      message rather than showing the static placeholders.
// =============================================================================

import { classify } from '../engine/index.js';
import { SPEC_METADATA } from '../engine/spec.js';
import type { Classification, LabInputs } from '../engine/types.js';
import { isClassifiable, load } from './form-state.js';
import { renderLabTable } from './lab-table.js';
import { renderPatternNarrative } from './pattern-renderer.js';

// The PDF is for subscribers. Anyone who gave us their email — at the results
// gate (data-source="results-gate") or the PDF form (data-source="pdf") — has
// SUBSCRIBED_KEY set. LEGACY_UNLOCK_KEY is the old PDF-gate flag, kept so a
// user mid-session across a deploy isn't stranded.
const SUBSCRIBED_KEY = 'ff-newsletter-subscribed';
const LEGACY_UNLOCK_KEY = 'ff-pdf-unlocked';

// -----------------------------------------------------------------------------
// Tiny DOM helpers (mirrors results.ts)
// -----------------------------------------------------------------------------

function setText(key: string, value: string): void {
  document.querySelectorAll<HTMLElement>(`[data-ff-bind="${key}"]`).forEach((el) => {
    el.textContent = value;
  });
}

function setHtml(key: string, value: string): void {
  document.querySelectorAll<HTMLElement>(`[data-ff-bind="${key}"]`).forEach((el) => {
    el.innerHTML = value;
  });
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

// -----------------------------------------------------------------------------
// Label maps (mirrors results.ts)
// -----------------------------------------------------------------------------

function labelLifeStage(stage: LabInputs['lifeStage']): string {
  switch (stage) {
    case 'non_pregnant_premenopausal':
      return 'non-pregnant premenopausal';
    case 'pregnant_t1':
      return 'pregnancy first trimester';
    case 'pregnant_t2':
      return 'pregnancy second trimester';
    case 'pregnant_t3':
      return 'pregnancy third trimester';
    case 'postpartum_within_6mo':
      return 'postpartum (within 6 months)';
    case 'perimenopausal':
      return 'perimenopausal';
    case 'postmenopausal':
      return 'postmenopausal';
    case 'none_of_these':
      return 'not specified';
  }
}

function formatGeneratedDate(d: Date = new Date()): string {
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()}`;
}

// -----------------------------------------------------------------------------
// Compose the "For clinician" line — concise list of submitted lab values
// -----------------------------------------------------------------------------

function clinicianValuesLine(inputs: LabInputs): string {
  const parts: string[] = [];
  if (inputs.ferritin !== undefined) parts.push(`Ferritin ${inputs.ferritin} μg/L`);
  if (inputs.hemoglobin !== undefined) parts.push(`Hb ${inputs.hemoglobin} g/L`);
  if (inputs.mcv !== undefined) parts.push(`MCV ${inputs.mcv} fL`);
  if (inputs.tsat !== undefined) parts.push(`TSAT ${inputs.tsat}%`);
  if (inputs.rdw !== undefined) parts.push(`RDW ${inputs.rdw}%`);
  if (inputs.mch !== undefined) parts.push(`MCH ${inputs.mch} pg`);
  if (inputs.serumIron !== undefined) parts.push(`Serum iron ${inputs.serumIron} μmol/L`);
  if (inputs.tibc !== undefined) parts.push(`TIBC ${inputs.tibc} μmol/L`);
  if (inputs.crp !== undefined) parts.push(`CRP ${inputs.crp} mg/L`);
  if (inputs.stfr !== undefined) parts.push(`sTfR ${inputs.stfr} mg/L`);
  if (inputs.retHe !== undefined) parts.push(`Reticulocyte Hb ${inputs.retHe} pg`);
  return parts.join(' · ') || 'No values entered.';
}

// -----------------------------------------------------------------------------
// Gate handlers
// -----------------------------------------------------------------------------

function isPdfUnlocked(): boolean {
  try {
    return (
      sessionStorage.getItem(SUBSCRIBED_KEY) === 'true' ||
      sessionStorage.getItem(LEGACY_UNLOCK_KEY) === 'true'
    );
  } catch {
    return false;
  }
}

function redirectToResults(): void {
  // Redirect to the email-form section of results so the user lands on
  // the gate they need to complete. The browser jumps to id="pdf" once
  // results.html paints.
  window.location.replace('results#pdf');
}

// -----------------------------------------------------------------------------
// Render
// -----------------------------------------------------------------------------

function renderHeader(result: Classification): void {
  setText('patternLayperson', result.laypersonName);
  setText(
    'pdfSub',
    `Personalized read of your iron labs · generated ${formatGeneratedDate()}`,
  );
  // Footer carries the version, generated date, pattern name, and contact.
  // Version is a plain "v1.0" — no "engine" wording — and lives only at
  // the bottom of the document, not on the cover.
  setText(
    'pdfFooterMeta',
    `Femme Fortified · Iron Lab Explainer · v${SPEC_METADATA.version} · Generated ${formatGeneratedDate()} · ${result.technicalName} · Questions or corrections: hello@femmefortified.com`,
  );
}

function renderClinicianValues(inputs: LabInputs): void {
  setText('abstractClinicianValues', clinicianValuesLine(inputs));
}

function renderNoInputs(): void {
  setText('patternLayperson', 'No lab values on file.');
  setText(
    'pdfSub',
    'Head back to step 1 to enter your age, life stage, and at least one lab value.',
  );
  // Replace the abstract slots with a single helpful message
  setHtml(
    'abstractPattern',
    '<a href="step-1" class="btn-primary">Start from step 1</a>',
  );
  ['abstractConfidence', 'abstractEvidence', 'abstractSeverity', 'abstractClinicianValues']
    .forEach((slot) => setText(slot, ''));
  setHtml('overviewParagraphs', '<p>Once you enter your lab values, your personalized read will appear here.</p>');
  setHtml('confidenceParagraphRich', '');
  setHtml('whatThisMeansIntro', '');
}

function render(result: Classification, inputs: LabInputs): void {
  document.body.dataset.ffPatternId = result.patternId;

  renderHeader(result);
  renderClinicianValues(inputs);
  renderLabTable(inputs, labelLifeStage(inputs.lifeStage));
  renderPatternNarrative(result, inputs);

  // Force-open every candidate card on the PDF preview so the rendered
  // markup matches the print output (closed <details> elements would have
  // their bodies hidden by the user-agent stylesheet, dropping the fit
  // summary + disambiguating test from the on-screen view + the printed
  // PDF). The compact-mode CSS hides the overview / next-step body so
  // each card stays small even when open.
  document
    .querySelectorAll<HTMLDetailsElement>('.candidate-list-compact .candidate-card')
    .forEach((card) => {
      card.open = true;
    });
}

// -----------------------------------------------------------------------------
// Entry
// -----------------------------------------------------------------------------

function init(): void {
  // 1. Email gate
  if (!isPdfUnlocked()) {
    redirectToResults();
    return;
  }

  // 2. Load inputs
  const stored = load();
  if (!stored || !isClassifiable(stored)) {
    renderNoInputs();
    return;
  }

  // 3. Classify and render
  const result = classify(stored as LabInputs);
  render(result, stored as LabInputs);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
